import { LayoutModule } from '@angular/cdk/layout';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { StoreModule } from '@ngrx/store';
import { NgxMatDateAdap } from '@app-core/services/custom-date-adapters/date-adapter';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorI18nService } from '../paginatorTranslate';
import { MaterialModule } from '../material.module';
import { reducers } from './reducers';
import { DatePipe } from '@angular/common';

// Components
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { CustomTooltipComponent } from './components/custom-tooltip/custom-tooltip.component';
import { HeaderComponent } from './components/header/header.component';
import { InputComponent } from './components/input/input.component';
import { LivestreamModalComponent } from './components/livestream-modal/livestream-modal.component';
import { LivestreamTimerComponent } from './components/livestream-timer/livestream-timer.component';
import { MapComponent } from './components/map/map.component';
import { MediaPlayerComponent } from './components/media-player/media-player.component';
import { NetworkStatusComponent } from './components/network-status/network-status.component';
import { SearchTripComponent } from './components/search-trip/search-trip.component';
import { SelectComponent } from './components/select/select.component';
import { SideNavigationComponent } from './components/side-navigation/side-navigation.component';
import { SlideToggleComponent } from './components/slide-toggle/slide-toggle.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { TaggingFilterV2Component } from './components/tagging-filter-v2/tagging-filter-v2.component';
import { TaggingFilterV3Component } from './components/tagging-filter-v3/tagging-filter-v3.component';
import { UserAssignTagsComponent } from './components/user-assign-tags/user-assign-tags.component';
import { UserAssignTagsGroupComponent } from './components/user-assign-tags-group/user-assign-tags-group.component';
import { CoachingTypeFilterComponent } from './components/coaching-type-filter/coaching-type-filter.component';
import { ConfiguartionFaqComponent } from './components/configuartion-faq/configuartion-faq.component';
import { GeofenceListingComponent } from '../asset-configuration/components/geofence/geofence-listing/geofence-listing.component';
import { GeofenceFilterListComponent } from '../asset-configuration/components/geofence/geofence-filter-list/geofence-filter-list.component';
import { GeofenceFilterSelectComponent } from '../asset-configuration/components/geofence/geofence-filter-select/geofence-filter-select.component';
import { GeofenceSettingsListingComponent } from '../asset-configuration/components/geofence/geofence-settings-listing/geofence-settings-listing.component';
import { GeofenceAssetTagListComponent } from '../asset-configuration/components/geofence/geofence-asset-tag-list/geofence-asset-tag-list.component';
import { GeofenceSelectionOnlyLayoutComponent } from '../asset-configuration/components/geofence/geofence-selection-only-layout/geofence-selection-only-layout.component';

// Pipes
import { AbbreviateNumberPipe } from './pipes/abbreviate-number/abbreviate-number.pipe';
import { DateConversionPipe } from './pipes/date-conversion/date-conversion.pipe';
import { CoachingTypePipe } from './pipes/coaching-type/coaching-type.pipe';
import { CoachingReasonPipe } from './pipes/coaching-reason/coaching-reason.pipe';
import { DateConversionPipeV2 } from './pipes/date-conversion-v2/date-conversion-v2.pipe';
import { DateFormatPipe } from './pipes/date-format/date-format.pipe';
import { DistancePipe } from './pipes/distance/distance.pipe';
import { DurationPipe } from './pipes/duration/duration.pipe';
import { Duration2Pipe } from './pipes/durationV2/duration2.pipe';
import { MinuteSecondsPipe } from './pipes/minute-seconds/minute-seconds.pipe';
import { FormatNumberPipe } from './pipes/format-number/format-number.pipe';

// Directives
import {
  NgxMatDatetimePickerModule,
  NgxMatTimepickerModule,
  NgxMatNativeDateModule,
  NgxMatDateAdapter,
} from '@angular-material-components/datetime-picker';

import { AssetAutocompleteComponent } from './components/asset-autocomplete/asset-autocomplete.component';
import { BookmarkedVideosComponent } from './components/bookmarked-videos/bookmarked-videos.component';
import { DriverImageComponent } from './components/driver-image/driver-image.component';
import { FeatureAnnouncementComponent } from './components/feature-announcement/feature-announcement.component';
import { IncidentModalComponent } from './components/incident-modal/incident-modal.component';
import { IncidentModalV2Component } from './components/incident-modal-v2/incident-modal-v2.component';
import { OrientationBlockerComponent } from './components/orientation-blocker/orientation-blocker.component';
import { SelectFleetComponent } from './components/select-fleet/select-fleet.component';
import { SensorProfileComponent } from './components/sensor-profile/sensor-profile.component';
import { UserSettingsComponent } from './components/user-settings/user-settings.component';
import { ShortcutsListComponent } from './components/shortcuts-list/shortcuts-list.component';
import { AlertComponent } from './components/alert/alert.component';
import { PortalSearchComponent } from './components/portal-search/portal-search.component';
import { IncidentSummaryComponent } from './components/incident-summary/incident-summary.component';
import { TimerComponent } from './components/timer/timer.component';
import { ChallengedIncidentsComponent } from './components/challenged-incidents/challenged-incidents.component';

import { ButtonLoaderDirective } from './directives/button-loader/button-loader.directive';
import { CheckPermissionsDirective } from './directives/check-permissions/check-permissions.directive';
import { ClickElsewhereDirective } from './directives/click-elsewhere/click-elsewhere.directive';
import { CustomTooltipDirective } from './directives/custom-tooltip/custom-tooltip.directive';
import { FixToggleDisableFormControlDirective } from './directives/fix-toggle-disable-form-control/fix-toggle-disable-form-control.directive';
import { FullScreenDirective } from './directives/full-screen/full-screen.directive';
import { HlsVideoPlayerDirective } from './directives/hls-video-player/hls-video-player.directive';
import { RefreshDatepickerDirective } from './directives/refresh-datepicker/refresh-datepicker.directive';
import { ViewportSizeDirective } from './directives/viewport-size/viewport-size.directive';
import { DisableRightClickDirective } from './directives/disable-rightclick/disable-rightclick.directive';
import { LivestreamService } from '@app-core/services/livestream/livestream.service';
import { PortalFaqComponent } from './components/portal-faq/portal-faq.component';
import { TwoFactorAuthenticationComponent } from './components/two-factor-authentication/two-factor-authentication.component';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { DynamicFormControlComponent } from '@app-asset-config/components/dynamic-form-control/dynamic-form-control.component';
import { LmFooterComponent } from './components/footers/lm-footer/lm-footer.component';
import { FooterDirective } from './directives/footer/footer.directive';
import { UserSecurityComponent } from './components/user-security/user-security.component';
import { SoleraFooterComponent } from './components/footers/solera-footer/solera-footer.component';
import { Mobi7FooterComponent } from './components/footers/mobi7-footer/mobi7-footer.component';
import { GeoBoxAUFooterComponent } from './components/footers/geoboxau-footer/geoboxau-footer.component';
import { AudioConsentComponent } from './components/audio-consent/audio-consent.component';
import { CookieConsentComponent } from './components/cookie-consent/cookie-consent.component';
import { FeedbackwidgetComponent } from './components/feedback-widget/feedback-widget.component';
import { DurationConversionPipe } from './pipes/durationConversion/duration-conversion.pipe';
import { CheckUiconfigsDirective } from './directives/check-uiconfigs/check-uiconfigs.directive';
import { AttributeAssignComponent } from './components/attribute-assign/attribute-assign.component';
import { ZcdemoFooterComponent } from './footers/zcdemo-footer/zcdemo-footer.component';
import { DriverAutoCompleteComponent } from './components/driver-auto-complete/driver-auto-complete.component';
import { StatusTimelineComponent } from './components/status-timeline/status-timeline.component';
import { MapBoxMapComponent } from './components/map-box-map/map-box-map.component';
import { IncidentRatioPipe } from './pipes/incident-ratio/incident-ratio.pipe';
import { AbbreviateStorageSpacePipe } from './pipes/abbreviate-storage-pipe/abbreviate-storage-space.pipe';
import { ConfirmDialogComponent } from './components/dialog/confirm-dialog/confirm-dialog.component';
import { RecognizedEventsComponent } from './components/recognized-events/recognized-events.component';
import { EventStreakComponent } from './components/event-streak/event-streak.component';
import { AIRecognizedEventsComponent } from './components/airecognized-events/airecognized-events.component';
import { SatrackFooterComponent } from './components/footers/satrack-footer/satrack-footer.component';
import { TaabiComponent } from './components/footers/taabi/taabi.component';
import { DurationListComponent } from './components/duration-list/duration-list.component';
import { DurationRangeComponent } from './components/duration-range/duration-range.component';
import { ThemeService } from '@app-core/services/theme/theme.service';
import { QuantatecComponent } from './components/footers/quantatec/quantatec.component';
import { MultiSelectComponent } from './components/multi-select/multi-select/multi-select.component';
import { RearviewsafetyFooterComponent } from './components/footers/rearviewsafety-footer/rearviewsafety-footer.component';
import { SafefleetFooterComponent } from './components/footers/safefleet-footer/safefleet-footer.component';
import { SsoDialogComponent } from './components/dialog/sso-dialog/sso-dialog.component';
import { GuidpointComponent } from './components/footers/guidepoint-footer/guidpoint/guidpoint.component';
import { TopEventStreakComponent } from './components/top-event-streak/top-event-streak.component';
import { DriverResponseRateComponent } from './components/driver-response-rate/driver-response-rate.component';
import { CheckFeatureFlagDirective } from './directives/check-feature-list/check-feature-flag.directive';
import { BatchCoachUpdationComponent } from './components/batch-coach-updation/batch-coach-updation.component';
import { AssignCoachComponent } from './components/assign-coach/assign-coach.component';
import { MatChipsModule } from '@angular/material/chips';
import { CoachingSelectionModalComponent } from './components/coaching-selection-modal/coaching-selection-modal.component';
import { CoachingConfirmationModalComponent } from './components/coaching-confirmation-modal/coaching-confirmation-modal.component';
import { DownloadReportComponent } from './components/download-report/download-report.component';
import { SelfCoachingEffectivenessReportDialogComponent } from './components/self-coaching-effectiveness-report-dialog/self-coaching-effectiveness-report-dialog.component';
import { MaticxFooterComponent } from './components/footers/maticx-footer/maticx-footer.component';
import { NotificationBannerComponent } from './components/notification-banner/notification-banner.component';
import { DpltelematicsFooterComponent } from './components/footers/dpltelematics-footer/dpltelematics-footer.component';
import { TimeLapseDvrModalComponentComponent } from './components/time-lapse-dvr-modal-component/time-lapse-dvr-modal-component.component';
import { SuprsendNotificationComponent } from './components/suprsend-notification/suprsend-notification.component';
import { NotificationComponent } from './components/notification-component/notification-component.component';
import { MFLoaderComponent } from './components/mf-loader/mf-loader.components';
import { CoachingConfigTabComponent } from '@app-asset-config/pages/coaching-config-tab/coaching-config-tab.component';
import { AutoCoachingEventSelectionComponent } from '@app-asset-config/components/auto-coaching-event-selection/auto-coaching-event-selection.component';
import { CoachingThresholdComponent } from '@app-asset-config/components/coaching-threshold/coaching-threshold.component';
import { InPersonEscalationComponent } from '@app-asset-config/components/in-person-escalation/in-person-escalation.component';
import { CoachingConfigHelpComponent } from './components/coaching-config-help/coaching-config-help.component';
import { UnsavedConfigurationsModalComponent } from './components/unsaved-confiurations-modal/unsaved-confiurations-modal.component';
import { AssetDriverSelectionModalComponent } from './components/asset-driver-selection-modal/asset-driver-selection-modal.component';
import { TalkToDataModalComponent } from './components/talk-to-data/talk-to-data-modal.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SaveReportModalComponent } from './components/save-report-modal/save-report-modal.component';
import { UnsavedReportModalComponent } from './components/unsave-report-modal/unsave-report-modal.component';
import { DeleteReportModalComponent } from './components/delete-report-modal/delete-report-modal.component';
import { TargatelematicsComponent } from './components/footers/targatelematics/targatelematics.component';
import { BottomNavigationComponent } from './components/bottom-navigation/bottom-navigation.component';
import { MobileDurationFilterComponent } from './components/mobile-duration-filter/mobile-duration-filter.component';
import { DownloadCustomReportModalComponent } from './components/download-custom-report-modal/download-custom-report-modal.component';
import { DownloadScheduleModalComponent } from './components/download-schedule-modal/download-schedule-modal.component';
import { TalkToDataChartViewComponent } from './components/talk-to-data-chart-view/talk-to-data-chart-view.component';
import { TalkToDataTableViewComponent } from './components/talk-to-data-table-view/talk-to-data-table-view.component';
import { GenerateInsightsModalComponent } from './components/generate-insights-modal/generate-insights-modal.component';
import { ConfirmDeleteDialogComponent } from './components/confirm-delete-dialog/confirm-delete-dialog.component';
import { FaceIdImageUrlPipe } from './pipes/face-id-image-url/face-id-image-url.pipe';
import { AssetUrlPipe } from './pipes/asset-url/asset-url.pipe';
import { WebRTCAudioCallModalComponent } from '@app-live-view/components/webrtc-audio-call-modal/webrtc-audio-call-modal.component';
import { WebRTCLivestreamModalComponent } from '@app-live-view/components/webrtc-livestream-modal/webrtc-livestream-modal.component';
// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    HeaderComponent,
    UserProfileComponent,
    AutocompleteComponent,
    SpinnerComponent,
    MapComponent,
    DistancePipe,
    DurationPipe,
    Duration2Pipe,
    DateFormatPipe,
    SelectComponent,
    InputComponent,
    MediaPlayerComponent,
    AbbreviateNumberPipe,
    DateConversionPipe,
    CoachingTypePipe,
    CoachingReasonPipe,
    DateConversionPipeV2,
    FormatNumberPipe,
    ClickElsewhereDirective,
    SlideToggleComponent,
    FixToggleDisableFormControlDirective,
    ButtonLoaderDirective,
    SearchTripComponent,
    CustomTooltipComponent,
    CustomTooltipDirective,
    SideNavigationComponent,
    DriverImageComponent,
    NetworkStatusComponent,
    FeatureAnnouncementComponent,
    BookmarkedVideosComponent,
    RefreshDatepickerDirective,
    AssetAutocompleteComponent,
    FullScreenDirective,
    SelectFleetComponent,
    HlsVideoPlayerDirective,
    LivestreamModalComponent,
    WebRTCAudioCallModalComponent,
    WebRTCLivestreamModalComponent,
    LivestreamTimerComponent,
    MinuteSecondsPipe,
    CheckPermissionsDirective,
    IncidentModalComponent,
    IncidentModalV2Component,
    AssetDriverSelectionModalComponent,
    CoachingSelectionModalComponent,
    CoachingConfirmationModalComponent,
    UnsavedConfigurationsModalComponent,
    SensorProfileComponent,
    ViewportSizeDirective,
    DisableRightClickDirective,
    OrientationBlockerComponent,
    AlertComponent,
    UserSettingsComponent,
    ShortcutsListComponent,
    PortalSearchComponent,
    IncidentSummaryComponent,
    TimerComponent,
    ChallengedIncidentsComponent,
    PortalFaqComponent,
    TwoFactorAuthenticationComponent,
    ChangePasswordComponent,
    DynamicFormControlComponent,
    LmFooterComponent,
    SoleraFooterComponent,
    Mobi7FooterComponent,
    GeoBoxAUFooterComponent,
    FooterDirective,
    UserSecurityComponent,
    AudioConsentComponent,
    CookieConsentComponent,
    FeedbackwidgetComponent,
    DurationConversionPipe,
    CheckUiconfigsDirective,
    TaggingFilterV2Component,
    CoachingTypeFilterComponent,
    AttributeAssignComponent,
    ZcdemoFooterComponent,
    DriverAutoCompleteComponent,
    StatusTimelineComponent,
    MapBoxMapComponent,
    ConfiguartionFaqComponent,
    IncidentRatioPipe,
    AbbreviateStorageSpacePipe,
    ConfirmDialogComponent,
    RecognizedEventsComponent,
    EventStreakComponent,
    AIRecognizedEventsComponent,
    SatrackFooterComponent,
    TaabiComponent,
    DurationListComponent,
    DurationRangeComponent,
    QuantatecComponent,
    MultiSelectComponent,
    RearviewsafetyFooterComponent,
    SafefleetFooterComponent,
    SsoDialogComponent,
    GuidpointComponent,
    TopEventStreakComponent,
    DriverResponseRateComponent,
    CheckFeatureFlagDirective,
    BatchCoachUpdationComponent,
    AssignCoachComponent,
    DownloadReportComponent,
    SelfCoachingEffectivenessReportDialogComponent,
    MaticxFooterComponent,
    NotificationBannerComponent,
    DpltelematicsFooterComponent,
    TimeLapseDvrModalComponentComponent,
    SuprsendNotificationComponent,
    NotificationComponent,
    MFLoaderComponent,
    CoachingConfigTabComponent,
    AutoCoachingEventSelectionComponent,
    CoachingThresholdComponent,
    InPersonEscalationComponent,
    CoachingConfigHelpComponent,
    TalkToDataModalComponent,
    SaveReportModalComponent,
    UnsavedReportModalComponent,
    DeleteReportModalComponent,
    TargatelematicsComponent,
    BottomNavigationComponent,
    MobileDurationFilterComponent,
    DownloadCustomReportModalComponent,
    DownloadScheduleModalComponent,
    TalkToDataTableViewComponent,
    TalkToDataChartViewComponent,
    GenerateInsightsModalComponent,
    GeofenceListingComponent,
    GeofenceSettingsListingComponent,
    GeofenceAssetTagListComponent,
    GeofenceFilterListComponent,
    GeofenceFilterSelectComponent,
    GeofenceSelectionOnlyLayoutComponent,
    TaggingFilterV3Component,
    UserAssignTagsComponent,
    UserAssignTagsGroupComponent,
    ConfirmDeleteDialogComponent,
    FaceIdImageUrlPipe,
    AssetUrlPipe,
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MaterialModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    LayoutModule,
    OverlayModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatNativeDateModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    StoreModule.forFeature('shared', reducers),
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  exports: [
    // Modules
    MaterialModule,
    FlexLayoutModule,
    LayoutModule,
    OverlayModule,
    FormsModule,
    ReactiveFormsModule,
    MatChipsModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatNativeDateModule,
    // Components
    HeaderComponent,
    AutocompleteComponent,
    SpinnerComponent,
    SelectComponent,
    InputComponent,
    MapComponent,
    SlideToggleComponent,
    CustomTooltipComponent,
    SideNavigationComponent,
    NetworkStatusComponent,
    DriverImageComponent,
    BookmarkedVideosComponent,
    AssetAutocompleteComponent,
    LivestreamModalComponent,
    WebRTCAudioCallModalComponent,
    WebRTCLivestreamModalComponent,
    LivestreamTimerComponent,
    IncidentModalComponent,
    IncidentModalV2Component,
    AssetDriverSelectionModalComponent,
    CoachingSelectionModalComponent,
    CoachingConfirmationModalComponent,
    UnsavedConfigurationsModalComponent,
    SensorProfileComponent,
    OrientationBlockerComponent,
    AlertComponent,
    UserSettingsComponent,
    ShortcutsListComponent,
    PortalSearchComponent,
    IncidentSummaryComponent,
    ChallengedIncidentsComponent,
    DynamicFormControlComponent,
    LmFooterComponent,
    CookieConsentComponent,
    FeedbackwidgetComponent,
    TimerComponent,
    TaggingFilterV2Component,
    CoachingTypeFilterComponent,
    AttributeAssignComponent,
    ZcdemoFooterComponent,
    DriverAutoCompleteComponent,
    StatusTimelineComponent,
    SoleraFooterComponent,
    MapBoxMapComponent,
    ConfiguartionFaqComponent,
    ConfirmDialogComponent,
    RecognizedEventsComponent,
    EventStreakComponent,
    AIRecognizedEventsComponent,
    SatrackFooterComponent,
    TaabiComponent,
    DurationListComponent,
    DurationRangeComponent,
    QuantatecComponent,
    MultiSelectComponent,
    SsoDialogComponent,
    TopEventStreakComponent,
    DriverResponseRateComponent,
    MFLoaderComponent,
    CoachingConfigTabComponent,
    TargatelematicsComponent,
    GeofenceListingComponent,
    GeofenceSettingsListingComponent,
    GeofenceAssetTagListComponent,
    GeofenceFilterListComponent,
    GeofenceFilterSelectComponent,
    GeofenceSelectionOnlyLayoutComponent,
    // Pipes
    DistancePipe,
    DurationPipe,
    Duration2Pipe,
    DateFormatPipe,
    DateConversionPipe,
    CoachingTypePipe,
    CoachingReasonPipe,
    DateConversionPipeV2,
    AbbreviateNumberPipe,
    AbbreviateStorageSpacePipe,
    FormatNumberPipe,
    DurationConversionPipe,
    MinuteSecondsPipe,
    IncidentRatioPipe,
    FaceIdImageUrlPipe,
    AssetUrlPipe,
    // Directives
    ClickElsewhereDirective,
    FixToggleDisableFormControlDirective,
    CustomTooltipDirective,
    ButtonLoaderDirective,
    RefreshDatepickerDirective,
    FullScreenDirective,
    HlsVideoPlayerDirective,
    CheckPermissionsDirective,
    ViewportSizeDirective,
    FooterDirective,
    CheckUiconfigsDirective,
    CheckFeatureFlagDirective,
    AssignCoachComponent,
    DisableRightClickDirective,
    NotificationBannerComponent,
    BottomNavigationComponent,
    MobileDurationFilterComponent,
    TaggingFilterV3Component,
    UserAssignTagsComponent,
    UserAssignTagsGroupComponent,
  ],
  entryComponents: [
    CustomTooltipComponent,
    FeatureAnnouncementComponent,
    DriverImageComponent,
    LivestreamModalComponent,
    WebRTCAudioCallModalComponent,
    WebRTCLivestreamModalComponent,
    IncidentModalComponent,
    IncidentModalV2Component,
    AssetDriverSelectionModalComponent,
    CoachingSelectionModalComponent,
    CoachingConfirmationModalComponent,
    UnsavedConfigurationsModalComponent,
    OrientationBlockerComponent,
    ShortcutsListComponent,
    PortalSearchComponent,
    UserSettingsComponent,
    ChangePasswordComponent,
    TwoFactorAuthenticationComponent,
    UserSecurityComponent,
    AudioConsentComponent,
    CookieConsentComponent,
    FeedbackwidgetComponent,
    StatusTimelineComponent,
    SsoDialogComponent,
    TalkToDataModalComponent,
    SaveReportModalComponent,
    UnsavedReportModalComponent,
    DeleteReportModalComponent,
    DownloadCustomReportModalComponent,
    DownloadScheduleModalComponent,
    TalkToDataTableViewComponent,
    TalkToDataChartViewComponent,
    GenerateInsightsModalComponent,
  ],
  providers: [
    { provide: NgxMatDateAdapter, useClass: NgxMatDateAdap },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorI18nService,
    },
    LivestreamService,
    DatePipe,
    ThemeService,
  ],
})
export class SharedModule {
  constructor(private themeService: ThemeService) {
    this.themeService.applyThemeToOverlay();
  }
}
